import { useEffect, useMemo, useRef, useState } from 'react';
import { COLOR_NAME, makeRulePuzzle, RULE_POINTS, SHAPE_NAME, type Token } from './rule';
import { elapsed, type DrillProps } from './meta';

const SPOTS: Record<number, [number, number][]> = {
  1: [[50, 50]],
  2: [[29, 50], [71, 50]],
  3: [[29, 31], [71, 31], [50, 71]],
  4: [[29, 29], [71, 29], [29, 71], [71, 71]],
};

export function TokenView({ t, size = 56 }: { t: Token; size?: number }) {
  const r = t.count === 1 ? 27 : 16;
  const solid = t.fill === 'solid';
  return (
    <svg
      className="token" width={size} height={size} viewBox="0 0 100 100" role="img"
      aria-label={`${COLOR_NAME[t.color]} ${SHAPE_NAME[t.shape]} ${t.count}개${solid ? '' : ' (빈 모양)'}`}
      style={{ color: `var(--tok-${t.color})` }}
    >
      {SPOTS[t.count].map(([x, y], i) => {
        const common = { fill: solid ? 'currentColor' : 'none', stroke: 'currentColor', strokeWidth: 5, strokeLinejoin: 'round' as const };
        if (t.shape === 'circle') return <circle key={i} cx={x} cy={y} r={r} {...common} />;
        if (t.shape === 'square') return <rect key={i} x={x - r * 0.85} y={y - r * 0.85} width={r * 1.7} height={r * 1.7} rx={3} {...common} />;
        if (t.shape === 'triangle') return <polygon key={i} points={`${x},${y - r} ${x + r},${y + r * 0.8} ${x - r},${y + r * 0.8}`} {...common} />;
        return <polygon key={i} points={`${x},${y - r} ${x + r},${y} ${x},${y + r} ${x - r},${y}`} {...common} />;
      })}
    </svg>
  );
}

export function RuleDrill({ level, seed, onDone }: DrillProps) {
  const puzzle = useMemo(() => makeRulePuzzle(level, seed), [level, seed]);
  const started = useRef(performance.now());
  const [index, setIndex] = useState(0);
  const [hits, setHits] = useState(0);
  const [board, setBoard] = useState(puzzle.examples);
  const [flash, setFlash] = useState<{ ok: boolean; truth: boolean } | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  const testsDone = index >= puzzle.tests.length;
  const current = puzzle.tests[index];

  const answer = (say: boolean) => {
    if (flash || testsDone) return;
    const ok = say === current.ok;
    setFlash({ ok, truth: current.ok });
    if (ok) setHits((h) => h + 1);
    window.setTimeout(() => {
      setBoard((b) => [...b, current]);
      setFlash(null);
      setIndex((i) => i + 1);
    }, ok ? 450 : 1300);
  };
  const answerRef = useRef(answer);
  answerRef.current = answer;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' || e.key === '1') answerRef.current(true);
      if (e.key === 'ArrowRight' || e.key === '2') answerRef.current(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const finish = () => {
    const ruleOk = picked === puzzle.answer;
    onDone({
      correct: hits + (ruleOk ? RULE_POINTS : 0),
      total: puzzle.tests.length + RULE_POINTS,
      duration_ms: elapsed(started.current),
      detail: { seed, rule: puzzle.answer, picked, classified: hits, tests: puzzle.tests.length },
    });
  };

  const latest = board.length - 1;
  const column = (ok: boolean) => (
    <div className={`rule-col ${ok ? 'yes' : 'no'}`}>
      <h3>{ok ? '✓ 맞음' : '✗ 아님'}</h3>
      <div className="token-grid">
        {board.map((e, i) => e.ok === ok && (
          <span key={i} className={`token-cell ${i === latest && i >= puzzle.examples.length ? 'fresh' : ''}`}><TokenView t={e.token} size={44} /></span>
        ))}
      </div>
    </div>
  );

  return (
    <div className="drill rule-drill">
      <div className="rule-board">{column(true)}{column(false)}</div>
      {!testsDone ? (
        <div className={`rule-ask ${flash ? (flash.ok ? 'ok' : 'miss') : ''}`}>
          <div className="drill-progress">{index + 1} / {puzzle.tests.length}</div>
          <div className="rule-current"><TokenView t={current.token} size={96} /></div>
          <p className="rule-feedback" aria-live="polite">
            {flash ? (flash.ok ? '정답!' : `땡 — 규칙에 ${flash.truth ? '맞는' : '안 맞는'} 카드였습니다`) : '이 카드는 규칙에 맞을까요?'}
          </p>
          <div className="two-buttons">
            <button type="button" className="btn big yes" onClick={() => answer(true)} disabled={!!flash}>✓ 맞음</button>
            <button type="button" className="btn big no" onClick={() => answer(false)} disabled={!!flash}>✗ 아님</button>
          </div>
        </div>
      ) : (
        <div className="rule-ask">
          <p className="rule-feedback"><b>분류 {hits} / {puzzle.tests.length}</b> — 이제 규칙이 무엇이었을까요?</p>
          <div className="choice-list">
            {puzzle.choices.map((c) => (
              <button
                key={c} type="button" disabled={picked !== null} onClick={() => setPicked(c)}
                className={`choice ${picked !== null && c === puzzle.answer ? 'right' : ''} ${picked === c && c !== puzzle.answer ? 'wrong' : ''}`}
              >
                {c}
              </button>
            ))}
          </div>
          {picked !== null && (
            <div className="drill-end">
              <p>{picked === puzzle.answer ? '규칙을 꿰뚫었습니다 (+2점)' : `규칙은 "${puzzle.answer}" 였습니다`}</p>
              <button type="button" className="btn btn-primary" onClick={finish}>결과 보기</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
